import { useMemo, useState, useEffect } from "react";
import ProductAssessmentItem from "./ProductAssessmentItem";
import AssessmentActions from "./AssessmentActions";
import BulkApprovalCheckbox from "./BulkApprovalCheckbox";
import ReasonNoteModal, { noteForSelectedReason } from "./ReasonNoteModal";
import { post } from "../../../../services/ApiService";
import toast, { Toaster } from "react-hot-toast";

const normalizeProductId = (id) => (id == null ? "" : String(id));

const applyDefaultReason = (item, indicator, templates) => {
  const title =
    item.status === "approve"
      ? "Valid Order"
      : item.status === "cancel"
      ? "Buyer Cancelled"
      : item.title;
  return {
    ...item,
    title,
    note: noteForSelectedReason(title, item.note, indicator, templates),
  };
};

const isNoteRequiredForReason = (status, title, indicator) =>
  (status === "approve" && title !== "Valid Order") ||
  (status === "cancel" && title !== "Buyer Cancelled") ||
  (status === "approve" && indicator === "Red");

const GslReviewActions = ({
  order,
  canApprove,
  canDecline,
  onComplete,
  setOpenEscalations,
}) => {
  const [approvedPro, setApprovedPro] = useState([]);
  const [cancelledPro, setCancelledPro] = useState([]);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isAllApproved, setIsAllApproved] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [currentProductId, setCurrentProductId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [reasonTemplates, setReasonTemplates] = useState([]);

  const reviewProducts = useMemo(() => {
    const seen = new Map();
    return (order?.products || []).map((product, index) => {
      const itemId = String(product.productId || index);
      const base = String(product.productMongoId || itemId);
      const count = (seen.get(base) || 0) + 1;
      seen.set(base, count);
      return {
        productId: count === 1 ? base : `${base}#${index}`,
        productName: product.title || product.name || product.sku || "Product",
        questions: [],
        itemId,
      };
    });
  }, [order?.products]);

  const assessment = useMemo(
    () => ({
      status: "COMPLETE",
      isReview: false,
      products: reviewProducts,
    }),
    [reviewProducts]
  );

  const effectiveProductIds = reviewProducts.map((product) =>
    normalizeProductId(product.productId)
  );

  const allProductsProcessed =
    effectiveProductIds.length > 0 &&
    approvedPro.length + cancelledPro.length === effectiveProductIds.length;

  const currentReason = reasons.find(
    (r) => normalizeProductId(r.productId) === normalizeProductId(currentProductId)
  );

  useEffect(() => {
    if (currentReason?.status) {
      setReasons((prev) =>
        prev.map((item) =>
          normalizeProductId(item.productId) === normalizeProductId(currentProductId)
            ? applyDefaultReason(item, order?.indicator, reasonTemplates)
            : item
        )
      );
    }
  }, [currentReason?.status, currentProductId, order?.indicator, reasonTemplates]);

  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    setIsAllApproved(isChecked);

    if (isChecked) {
      setIsModalVisible(true);
      setCurrentProductId("BULK_ACTION");
      setReasons([
        {
          productId: "BULK_ACTION",
          status: "approve",
          title: "",
          note: "",
        },
      ]);
    } else {
      setApprovedPro([]);
    }
  };

  const validateReasonsBeforeSubmit = () => {
    const processedIds = [...approvedPro, ...cancelledPro].map(normalizeProductId);

    for (const productId of processedIds) {
      const reason = reasons.find(
        (r) => normalizeProductId(r.productId) === productId
      );

      if (!reason?.title || !reason?.status) {
        return "Please provide a reason for each approved or declined product.";
      }

      if (
        isNoteRequiredForReason(reason.status, reason.title, order?.indicator) &&
        !reason.note?.trim()
      ) {
        return "Note is required for the selected reason.";
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateReasonsBeforeSubmit();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const body = {
        approved: approvedPro.map(normalizeProductId),
        canceled: cancelledPro.map(normalizeProductId),
        reasons: reasons.map((reason) => ({
          ...reason,
          productId: normalizeProductId(reason.productId),
        })),
        lines: reviewProducts.map((product) => ({
          key: normalizeProductId(product.productId),
          itemId: product.itemId,
        })),
        orderId: order._id,
      };
      setIsSubmit(true);
      const res = await post("api/orders/gsl/review", body);
      setIsSubmit(false);
      if (res.success) {
        toast.success(res.message || "Order review completed");
        setApprovedPro([]);
        setCancelledPro([]);
        setReasons([]);
        if (Array.isArray(res.openEscalations) && setOpenEscalations) {
          setOpenEscalations(res.openEscalations);
        }
        if (onComplete && res.order) {
          onComplete(res.order);
        }
      } else {
        toast.error(res.error || "Failed to update order");
      }
    } catch (error) {
      setIsSubmit(false);
      toast.error(
        error?.response?.data?.error || "An error occurred. Please try again."
      );
    }
  };

  const openAssessmentActionModal = (productId, status) => {
    const normalizedProductId = normalizeProductId(productId);
    setIsModalVisible(true);
    setCurrentProductId(normalizedProductId);
    setReasons((prev) => {
      const index = prev.findIndex(
        (item) => normalizeProductId(item.productId) === normalizedProductId
      );
      if (index !== -1) {
        const updatedReasons = [...prev];
        updatedReasons[index] = {
          productId: normalizedProductId,
          status,
          title: "",
          note: "",
        };
        return updatedReasons;
      }
      return [
        ...prev,
        {
          productId: normalizedProductId,
          status,
          title: "",
          note: "",
        },
      ];
    });
  };

  const handleModalCancel = () => {
    if (currentProductId === "BULK_ACTION") {
      setIsAllApproved(false);
      setApprovedPro([]);
    }
    setCurrentProductId(null);
    setIsModalVisible(false);
  };

  const handleOrderAssessmentConfirm = () => {
    const reason = reasons.find(
      (r) => normalizeProductId(r.productId) === normalizeProductId(currentProductId)
    );

    if (!reason?.title || !reason?.status) {
      toast.error("Please select a reason.");
      return;
    }

    const { status, title, note } = reason;

    if (
      isNoteRequiredForReason(status, title, order?.indicator) &&
      !note?.trim()
    ) {
      toast.error("Note is required for the selected reason.");
      return;
    }

    if (currentProductId === "BULK_ACTION") {
      const newReasons = effectiveProductIds.map((productId) => ({
        productId,
        status,
        title,
        note,
      }));
      setReasons(newReasons);
      setApprovedPro(effectiveProductIds);
      setCancelledPro([]);
    } else if (status === "approve") {
      const normalizedProductId = normalizeProductId(currentProductId);
      setApprovedPro((prev) => [
        ...new Set([...prev.map(normalizeProductId), normalizedProductId]),
      ]);
      setCancelledPro((prev) =>
        prev
          .map(normalizeProductId)
          .filter((id) => id !== normalizedProductId)
      );
    } else if (status === "cancel") {
      const normalizedProductId = normalizeProductId(currentProductId);
      setCancelledPro((prev) => [
        ...new Set([...prev.map(normalizeProductId), normalizedProductId]),
      ]);
      setApprovedPro((prev) =>
        prev
          .map(normalizeProductId)
          .filter((id) => id !== normalizedProductId)
      );
    }

    setIsModalVisible(false);
    setCurrentProductId(null);
  };

  return (
    <div className="card mb-3">
      <Toaster position="top-right" />
      <div className="card-header d-flex align-items-center gap-2">
        <i className="fas fa-exclamation-triangle text-warning"></i>
        <strong>GSL Pharmacist Review Required</strong>
      </div>
      <div className="card-body">
        <div className="alert alert-warning mb-3" style={{ fontSize: "14px" }}>
          <i className="fas fa-info-circle me-2"></i>
          This order exceeds the permitted repeat purchase limit and requires
          pharmacist approval before dispatch. Please review each product below.
        </div>

        {!reviewProducts.length ? (
          <div className="alert alert-secondary mb-0" style={{ fontSize: "14px" }}>
            No products found on this order.
          </div>
        ) : (
          <>
            {reviewProducts.length > 1 && (
              <BulkApprovalCheckbox
                isAllApproved={isAllApproved}
                handleCheckboxChange={handleCheckboxChange}
              />
            )}

            {reviewProducts.map((proAssess, index) => (
              <ProductAssessmentItem
                key={proAssess.productId}
                proAssess={proAssess}
                index={index}
                assessment={assessment}
                order={order}
                approvedPro={approvedPro}
                cancelledPro={cancelledPro}
                canApprove={canApprove}
                canDecline={canDecline}
                openAssessmentActionModal={openAssessmentActionModal}
              />
            ))}

            <AssessmentActions
              allProductsProcessed={allProductsProcessed}
              handleSubmit={handleSubmit}
              isSubmit={isSubmit}
              order={order}
              cancelledPro={cancelledPro}
              approvedPro={approvedPro}
            />
          </>
        )}
      </div>

      <ReasonNoteModal
        open={isModalVisible}
        onOk={handleOrderAssessmentConfirm}
        onCancel={handleModalCancel}
        status={currentReason?.status}
        reasonTitle={currentReason?.title}
        note={currentReason?.note}
        onReasonChange={(value) => {
          setReasons((prev) =>
            prev.map((item) =>
              normalizeProductId(item.productId) ===
              normalizeProductId(currentProductId)
                ? {
                    ...item,
                    title: value,
                    note: noteForSelectedReason(
                      value,
                      item.note,
                      order?.indicator,
                      reasonTemplates
                    ),
                  }
                : item
            )
          );
        }}
        onTemplatesChange={setReasonTemplates}
        onNoteChange={(value) => {
          setReasons((prev) =>
            prev.map((item) =>
              normalizeProductId(item.productId) ===
              normalizeProductId(currentProductId)
                ? { ...item, note: value }
                : item
            )
          );
        }}
      />
    </div>
  );
};

export default GslReviewActions;
